import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Project } from '../../../../../../../../../shared/models';
import {
  DeleteProjectMaterialTestRequest,
  FetchProjectMaterialTestListRequest,
} from '../../../../../../../core/projects.actions';
import { projectMaterialTestListInfo } from '../../../../../../../core/projects.selectors';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../shared/components';
import { MaterialTestCreateEditPopupComponent } from '../../../../../../../pages/projects-details-page/components/tab-screens/settings/material-test/material-test-create-edit-popup/material-test-create-edit-popup.component';

@Component({
    selector: 'app-material-test-list',
    templateUrl: './material-test-list.component.html',
    styleUrls: ['./material-test-list.component.scss'],
    standalone: false
})
export class MaterialTestListComponent implements OnInit, OnDestroy {
  projectId: string | null = null;

  public dataSource: any[] = [];
  public filteredDataSource: any[] = [];
  public originalDataSource: any[] = [];
  public displayedColumns: string[] = ['name', 'actions'];
  public totalRecords = 0;
  public pagination = {
    pageIndex: 0,
    pageSize: 10,
  };
  public currentPage = 1;
  public itemsPerPage = 10;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private dialog: MatDialog,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (this.route.snapshot.paramMap.has('projectId')) {
      this.projectId = this.route.snapshot.paramMap.get('projectId');
    }

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        if (qp.subTab === 'material-tests') {
          this.fetchRecords();
        }
      });

    this.store
      .select(projectMaterialTestListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          data = JSON.parse(JSON.stringify(data));
          this.currentPage = 1;

          if (data && data?.list?.length > 0) {
            this.originalDataSource = data?.list;
            this.filteredDataSource = [...this.originalDataSource];
            this.totalRecords = data.totalRecords || this.originalDataSource.length;

            this.paginateData(true);
          } else {
            this.originalDataSource = [];
            this.filteredDataSource = [];
            this.dataSource = [];
            this.totalRecords = 0;
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

  fetchRecords(): void {
    if (this.projectId) {
      this.store.dispatch(
        FetchProjectMaterialTestListRequest({
          payload: {
            projectId: this.projectId,
            qp: { start: 0, total: 1000 },
          },
        })
      );
    }
  }

  onPageChanged(page: number): void {
    this.currentPage = page;
    this.pagination.pageIndex = page - 1;
    this.paginateData();
  }

  paginateData(reset = false): void {
    if (reset) {
      this.currentPage = 1;
    }
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, this.filteredDataSource.length);
    this.dataSource = this.filteredDataSource.slice(startIndex, endIndex);
  }

  openRenamePopup(item: any): void {
    const project = this.projectId ? ({ id: this.projectId } as Project) : null;
    const dialogRef = this.dialog.open(MaterialTestCreateEditPopupComponent, {
      width: '532px',
      height: '400px',
      disableClose: false,
      autoFocus: false,
      data: { project, item },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.fetchRecords();
      }
    });
  }

  deleteItem(item: any): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Delete',
        `Are you sure you want to delete "${item?.name || item?.value}"?`,
      ),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && this.projectId) {
        const testId = item?.id ?? item?.testId;
        if (testId) {
          this.store.dispatch(
            DeleteProjectMaterialTestRequest({
              projectId: this.projectId,
              testId: String(testId),
            })
          );
        }
      }
    });
  }
}
